// ============================================================================
// FSA NOTES SERVICE  (FSA-2.2)
// ============================================================================

import { get, put, post } from '../../client/axiosClient';
import { fsaNotesEndpoints } from '../../endpoints/fsa/fsaNotes.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  EngagementNoteData,
  EngagementCustomClass,
  NoteListItem,
  NoteHydrate,
  SaveNoteDataRequest,
  ExcludeNoteRequest,
  SetNoteNumberRequest,
  AutoNumberNotesResult,
} from '../../types/fsa/noteData.types';

export interface TransitionRequest {
  rowVersion?: number | null;
}

class FsaNotesService {
  async list(engagementId: ID): Promise<ApiResponse<NoteListItem[]>> {
    const r = await get<ApiResponse<NoteListItem[]>>(fsaNotesEndpoints.listByEngagement(engagementId));
    return r.data;
  }

  /**
   * FSA-2.2 step 4b — every engagement-time custom class declared across
   * the engagement's saved notes, with its owning schedule + live ledger
   * count. Drives the TB classifier's tree picker so the auditor can map
   * a TB ledger directly to a custom bucket.
   */
  async listCustomClasses(engagementId: ID): Promise<ApiResponse<EngagementCustomClass[]>> {
    const r = await get<ApiResponse<EngagementCustomClass[]>>(
      fsaNotesEndpoints.listCustomClasses(engagementId),
    );
    return r.data;
  }

  async hydrate(engagementId: ID, scheduleId: ID): Promise<ApiResponse<NoteHydrate>> {
    const r = await get<ApiResponse<NoteHydrate>>(fsaNotesEndpoints.hydrate(engagementId, scheduleId));
    return r.data;
  }

  async save(
    engagementId: ID,
    scheduleId: ID,
    request: SaveNoteDataRequest,
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await put<ApiResponse<EngagementNoteData>>(fsaNotesEndpoints.save(engagementId, scheduleId), request);
    return r.data;
  }

  async submit(
    engagementId: ID,
    scheduleId: ID,
    request: TransitionRequest = {},
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await post<ApiResponse<EngagementNoteData>>(
      fsaNotesEndpoints.submit(engagementId, scheduleId), request,
    );
    return r.data;
  }

  async approve(
    engagementId: ID,
    scheduleId: ID,
    request: TransitionRequest = {},
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await post<ApiResponse<EngagementNoteData>>(
      fsaNotesEndpoints.approve(engagementId, scheduleId), request,
    );
    return r.data;
  }

  async reopen(
    engagementId: ID,
    scheduleId: ID,
    request: TransitionRequest = {},
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await post<ApiResponse<EngagementNoteData>>(
      fsaNotesEndpoints.reopen(engagementId, scheduleId), request,
    );
    return r.data;
  }

  /**
   * FSA-2.2 — mark a Note as not-applicable. Returns the API response so
   * callers can read 409 status to drive the "Clear saved data?"
   * ConfirmDialog. On 409 the backend's error code is
   * `requires_clear_confirmation` — frontend re-calls with
   * `clearSavedData = true`.
   */
  async exclude(
    engagementId: ID,
    scheduleId: ID,
    request: ExcludeNoteRequest,
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await post<ApiResponse<EngagementNoteData>>(
      fsaNotesEndpoints.exclude(engagementId, scheduleId), request,
    );
    return r.data;
  }

  async include(
    engagementId: ID,
    scheduleId: ID,
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await post<ApiResponse<EngagementNoteData>>(
      fsaNotesEndpoints.include(engagementId, scheduleId), {},
    );
    return r.data;
  }

  async setNoteNumber(
    engagementId: ID,
    scheduleId: ID,
    request: SetNoteNumberRequest,
  ): Promise<ApiResponse<EngagementNoteData>> {
    const r = await put<ApiResponse<EngagementNoteData>>(
      fsaNotesEndpoints.setNoteNumber(engagementId, scheduleId), request,
    );
    return r.data;
  }

  async autoNumber(
    engagementId: ID,
  ): Promise<ApiResponse<AutoNumberNotesResult>> {
    const r = await post<ApiResponse<AutoNumberNotesResult>>(
      fsaNotesEndpoints.autoNumber(engagementId), {},
    );
    return r.data;
  }

  /**
   * FSA-2.3 — Move a note to a specific position; backend reshuffles
   * the rest (Variant 1 semantic). Target position is clamped to N
   * when > total included notes.
   */
  async moveToPosition(
    engagementId: ID,
    scheduleId: ID,
    targetPosition: number,
  ): Promise<ApiResponse<AutoNumberNotesResult>> {
    const r = await post<ApiResponse<AutoNumberNotesResult>>(
      fsaNotesEndpoints.moveToPosition(engagementId, scheduleId),
      { targetPosition },
    );
    return r.data;
  }

  /**
   * FSA-2.3 Roll Forward — pull the prior engagement's saved note workspace
   * into this engagement's note (line item names, classifications, ledger
   * mappings, manual disclosure rows, per-disclosure carry rules). Caller
   * is responsible for the ConfirmDialog gate when the note already has
   * saved content.
   */
  async importFromPriorYear(
    engagementId: ID,
    scheduleId: ID,
  ): Promise<ApiResponse<import('../../types/fsa/rollForward.types').ImportFromPyResult>> {
    const r = await post<ApiResponse<import('../../types/fsa/rollForward.types').ImportFromPyResult>>(
      fsaNotesEndpoints.importFromPriorYear(engagementId, scheduleId),
      {},
    );
    return r.data;
  }
}

export const fsaNotesService = new FsaNotesService();
export default fsaNotesService;
